import setEvents from '@design/objects/form/shared/field/assets/set-events'
import MobileBottomPanel, { Events as MobileBottomPanelEvents } from '@ui/MobileBottomPanel/component'
import * as coreCommon from 'assets/core/js/common'
import Autocomplete, { type AutocompleteResultValue, type AutocompleteConfig as BaseAutocompleteConfig } from 'assets/core/js/module/autocomplete'
import type { MobileBottomPanelType } from '@ui/MobileBottomPanel/component'

interface SearchAutocompleteResultValue extends AutocompleteResultValue {
  geo: AutocompleteResultValue[]
  product: AutocompleteResultValue[]
  brand: AutocompleteResultValue[]
  sessionToken?: string
  meta: {
    context: Record<string, string>
    name: string
    type: string
  }
}

interface SearchAutocompleteResult {
  data: SearchAutocompleteResultValue
  html: string
}

interface SearchAutocompleteJsonData extends Omit<AutocompleteResultValue, 'id' | 'type'> {
  token: string
  detail: {
    id: number
    category: string
    source?: string
  }
}

interface SearchReset {
  el: HTMLElement
  show: () => void
  hide: () => void
}

interface SearchAutocompleteSuggestions {
  el: HTMLElement
  show: () => void
  hide: () => void
}

type CallbackSearchSuggestionClick = (element: HTMLElement) => void
type CallbackLinkClick = (type: string, value: string, userInput: string) => void

interface AutocompleteConfig extends BaseAutocompleteConfig {
  callbackSearchSuggestionClick: CallbackSearchSuggestionClick
  callbackLinkClick: CallbackLinkClick
}

export const Events = {
  AUTOCOMPLETE_SET_DATA: 'autocomplete.set.data',
  AUTOCOMPLETE_MODAL_OPENED: 'autocomplete.modal.opened',
  AUTOCOMPLETE_MODAL_CLOSED: 'autocomplete.modal.closed',
  AUTOCOMPLETE_ITEM_SELECTED: 'autocomplete.item.selected',
}

export class SearchAutocomplete extends Autocomplete {
  element!: Element
  mobileBottomPanel!: MobileBottomPanelType
  dataEl!: HTMLInputElement
  mirrorInputEl!: HTMLInputElement
  reset!: SearchReset | null
  searchSuggestions!: SearchAutocompleteSuggestions | null
  sessionToken!: string | null
  events!: {
    inputClick?(e?: MouseEvent | TouchEvent | Event): void
    inputKeyUp?(e?: KeyboardEvent | TouchEvent | Event): void
    inputInput?(): void
    searchInputKeyUp?(this: void, e?: KeyboardEvent | TouchEvent | Event): void
  }

  config!: Partial<AutocompleteConfig>

  selection?: SearchAutocompleteResultValue

  constructor(element: string | HTMLElement, userConfig?: Partial<AutocompleteConfig>) {
    const config: Partial<BaseAutocompleteConfig> = {
      callbackSelect: (value: SearchAutocompleteResultValue) => {
        if (this.sessionToken) {
          value.sessionToken = this.sessionToken
        }

        if (value.type !== 'query') {
          this.dataEl.value = JSON.stringify(value)
          this.selection = value
        }

        if (value.type === 'query') {
          this.dataEl.value = ''
          this.selection = undefined
        }

        this.element.dispatchEvent(
          new CustomEvent(Events.AUTOCOMPLETE_ITEM_SELECTED, {
            detail: {
              item: value,
            },
          })
        )
      },
      callbackQuery: () => {
        this.searchSuggestions?.hide()
        this.reset?.show()
      },
      callbackDisplayResults: () => {
        this.searchSuggestions?.hide()
        this.reset?.show()
      },
      callbackEmptyInput: () => {
        this.dataEl.value = ''
        this.searchSuggestions?.show()
        this.reset?.hide()
      },
      callbackOpen: () => {
        document.body.classList.add('search-form__autocomplete--opened')

        this.element.querySelectorAll<HTMLElement>('[data-tabIndex]').forEach((el) => {
          el.setAttribute('tabIndex', el.getAttribute('data-tabIndex') as string)
        })
      },
      callbackClose: () => {
        const currentlyOpened = this.element.classList.contains('autocomplete-active')
        document.body.classList.remove('search-form__autocomplete--opened')
        this.element.classList.remove('autocomplete-active')

        this.element.querySelectorAll<HTMLElement>('[data-tabIndex]').forEach((el) => {
          el.setAttribute('tabIndex', '-1')
        })

        if (currentlyOpened) {
          this.element.dispatchEvent(new CustomEvent(Events.AUTOCOMPLETE_MODAL_CLOSED))
        }
      },
      callbackClick: () => {
        this.element.dispatchEvent(new CustomEvent(Events.AUTOCOMPLETE_MODAL_OPENED))
        if (coreCommon.isMobile() || coreCommon.isTablet()) {
          this.inputEl.blur()
          this.mobileBottomPanel.open()
        }
      },
      closeIfMinLengthBelow: false,
    }

    super(element, Object.assign({}, config, userConfig))
    this.config.callbackSearchSuggestionClick = userConfig?.callbackSearchSuggestionClick
    this.dataEl = document.getElementById(`${this.inputEl.id}-data`) as HTMLInputElement
    this.setupMirrorInput()
    this.initMobileBottomPanel()
    this.initReset()
    this.initSearchSuggestions()
    this.sessionToken = null
    this.initSelection()

    if (this.inputEl.value !== '') {
      this.inputEl.dispatchEvent(new CustomEvent('change'))
    }
  }

  private setupMirrorInput(): void {
    this.mirrorInputEl = this.element.querySelector('.autocomplete-input-mirror input') as HTMLInputElement

    if (this.inputEl.value !== '') {
      this.mirrorInputEl.value = this.inputEl.value
    }

    this.mirrorInputEl.addEventListener('input', (e) => {
      this.inputEl.value = (e.target as HTMLInputElement).value

      const newEvent = new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        data: (e as InputEvent).data,
      })

      this.inputEl.value !== '' && this.searchSuggestions && this.searchSuggestions.hide()
      this.inputEl.dispatchEvent(newEvent)
    })

    // @ts-expect-error we want the overload
    this.mirrorInputEl.addEventListener('keyup', this.events.searchInputKeyUp)

    this.mirrorInputEl.addEventListener('blur', () => {
      this.inputEl.blur()
    })
  }

  private initSearchSuggestions(): void {
    const el = this.element.querySelector<HTMLElement>('.autocomplete-search-suggestions')

    if (!el) {
      return
    }

    this.searchSuggestions = {
      el,
      show: (): void => {
        el.removeAttribute('hidden')
      },
      hide: (): void => {
        if (this.inputEl.value === '') {
          return
        }

        el.setAttribute('hidden', 'hidden')
      },
    }

    const callback = this.config.callbackSearchSuggestionClick
    const links = el.querySelectorAll<HTMLElement>('.list > *')
    links.forEach((link: HTMLElement) => {
      link.addEventListener('click', () => {
        this.close()
        if (callback) {
          callback(link)
        }
      })
    })
  }

  private initReset(): void {
    const resetFieldEl = document.createElement('div')
    resetFieldEl.className = 'field__reset'
    this.inputEl.value === '' && resetFieldEl.setAttribute('hidden', 'hidden')

    const clearInput = (e: Event): void => {
      e.preventDefault()
      e.stopPropagation()

      this.previousRequest?.abort()

      this.inputEl.value = ''
      this.dataEl.value = ''
      this.selection = undefined
      this.searchValue = ''
      this.inputEl.dispatchEvent(new CustomEvent('change'))

      this.reset?.hide()

      this.resultsEl.innerHTML = ''
      this.searchSuggestions?.show()
    }

    resetFieldEl.addEventListener('click', clearInput)
    resetFieldEl.addEventListener('touchstart', clearInput)

    this.inputEl.parentElement?.append(resetFieldEl)

    this.reset = {
      el: resetFieldEl,
      show: (): void => {
        resetFieldEl.removeAttribute('hidden')
      },
      hide: (): void => {
        resetFieldEl.setAttribute('hidden', 'hidden')
      },
    }
  }

  initEvents(): void {
    super.initEvents()

    setEvents(this.inputEl)

    // On mobile, prevent the keyboard from opening on the original input.
    this.inputEl.addEventListener('mousedown', (event) => {
      if (coreCommon.isMobile() || coreCommon.isTablet()) {
        event.preventDefault()
      }
    })
    this.inputEl.addEventListener('pointerup', () => {
      if (this.searchSuggestions) {
        this.resultsVisible = true
        this.open()
      }
    })

    this.inputEl.addEventListener('change', (e) => {
      this.mirrorInputEl.value = (e.target as HTMLInputElement).value
    })

    this.element.querySelector<HTMLElement>(`label[for="${this.inputEl.id}"]`)?.addEventListener('pointerup', () => {
      this.config.callbackClick?.()

      this.inputEl.dispatchEvent(new PointerEvent('pointerup'))
    })

    // @ts-expect-error we want the overload
    this.inputEl.addEventListener(Events.AUTOCOMPLETE_SET_DATA, (e: CustomEvent<SearchAutocompleteJsonData>) => {
      const { token, name, nameHighlight, description, detail } = e.detail

      this.dataEl.value = JSON.stringify({
        token,
        name,
        nameHighlight,
        description,
        detail,
      })

      this.inputEl.value = name
    })

    this.events.searchInputKeyUp = (e: KeyboardEvent) => {
      const rows = this.resultsEl.getElementsByClassName('autocomplete-result')

      if (e.key === 'ArrowUp' && this.selectedRow >= 0 && this.resultsVisible) {
        --this.selectedRow
        // @ts-expect-error object is defined
        rows[this.selectedRow + 1].classList.remove('autocomplete-result-focus')

        if (this.selectedRow >= 0) {
          // @ts-expect-error object is defined
          rows[this.selectedRow].classList.add('autocomplete-result-focus')

          // @ts-expect-error object is defined
          this.inputEl.value = rows[this.selectedRow].getAttribute('data-value')
        }
      } else if (e.key === 'ArrowDown' && this.selectedRow < rows.length - 1 && this.resultsVisible) {
        ++this.selectedRow
        if (this.selectedRow > 0) {
          // @ts-expect-error object is defined
          rows[this.selectedRow - 1].classList.remove('autocomplete-result-focus')
        }

        // @ts-expect-error object is defined
        rows[this.selectedRow].classList.add('autocomplete-result-focus')

        // @ts-expect-error object is defined
        this.inputEl.value = rows[this.selectedRow].getAttribute('data-value')
      } else if (e.key === 'Enter') {
        if (rows[this.selectedRow]) {
          // @ts-expect-error object is defined
          rows[this.selectedRow].click()
        }
      } else if (e.key === 'Escape') {
        if (this.resultsVisible) {
          this.close()
        }
      } else if (!['Shift', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
        this.dataEl.value = ''
        this.selection = undefined
      }
    }

    // @ts-expect-error we want the overload
    this.inputEl.removeEventListener('keyup', this.events.inputKeyUp)
    this.inputEl.addEventListener('keyup', this.events.searchInputKeyUp)

    this.element.addEventListener(Events.AUTOCOMPLETE_ITEM_SELECTED, () => {
      this.close()
    })
  }

  displayResults(data: SearchAutocompleteResult): void {
    if (data.data?.meta && data.data.sessionToken) {
      this.sessionToken = data.data.sessionToken
    }
    this.config.callbackDisplayResults?.(this.searchValue)
    this.resultsEl.innerHTML = ''

    // force order of keys
    const dataKeys = ['geo', 'product', 'brand']
    let hasResults = false

    dataKeys.forEach((key) => {
      const resultData = data.data
      // @ts-expect-error elements are defined
      if (resultData?.[key] && Array.isArray(resultData[key]) && resultData[key].length > 0) {
        if (!hasResults) {
          hasResults = true
        }

        this.resultsEl.innerHTML = data.html

        this.resultsEl.querySelectorAll<HTMLElement>('.autocomplete-result').forEach((el) => {
          el.addEventListener('click', (evt) => {
            const value = el.getAttribute('data-value') as string
            const type = el.getAttribute('data-type') as keyof SearchAutocompleteResultValue
            const index = el.getAttribute('data-index') as string | number
            const isLink = el.getAttribute('data-is-link') as boolean | null
            this.inputEl.value = value

            if (this.config.callbackSelect && !isLink) {
              // @ts-expect-error elements are defined
              this.config.callbackSelect(type === 'meta' ? resultData[type] : resultData[type][index])
            }

            if (isLink) {
              this.config.callbackLinkClick?.(type, value, this.searchValue)

              const link = el.querySelector('a') as HTMLAnchorElement
              const target = link.getAttribute('target') ?? '_self'
              window.open(link.getAttribute('href') as string, target)
              evt.preventDefault()
            }

            this.close()
          })
        })
      }
    })

    if (hasResults) {
      if (this.inputEl.value !== '') {
        this.open()
      }
    }
  }

  close(): void {
    super.close()

    this.mobileBottomPanel.close()
  }

  private initMobileBottomPanel(): void {
    const mobileBottomPanelEl = document.getElementById(`${this.inputEl.id}-mobilebottompanel`)

    if (!mobileBottomPanelEl) {
      return
    }

    this.mobileBottomPanel = MobileBottomPanel(mobileBottomPanelEl)

    const autocompleteMirrorContainerEl = this.element.querySelector<HTMLElement>('.autocomplete-input-mirror')

    if (!this.mobileBottomPanel || !autocompleteMirrorContainerEl) {
      return
    }

    mobileBottomPanelEl.addEventListener(MobileBottomPanelEvents.MOBILE_BOTTOM_PANEL_CLOSED, () => {
      this.close()
    })

    mobileBottomPanelEl.addEventListener(MobileBottomPanelEvents.MOBILE_BOTTOM_PANEL_OPENED, () => {
      setTimeout(() => {
        this.mirrorInputEl.focus()
      }, 250)
    })
  }

  getQueryParameters(): Record<string, string> {
    const params = super.getQueryParameters()
    if (this.sessionToken !== null) {
      params.sessionToken = this.sessionToken
    }

    return params
  }

  /**
   * Initialize the selection based on the value of dataEl.
   */
  private initSelection(): void {
    const raw = this.dataEl.value ? JSON.parse(this.dataEl.value) : undefined
    this.selection = undefined
    if (raw && typeof raw === 'object' && 'detail' in raw && typeof raw.detail === 'object') {
      this.selection = raw.detail as SearchAutocompleteResultValue
    }
  }
}

export default (element: string | HTMLElement, userConfig?: Partial<AutocompleteConfig>): SearchAutocomplete => {
  return new SearchAutocomplete(element, userConfig)
}
